import { Table, TableSorting } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { DATE_FORMAT } from "@/constants/datetime";
import { useTable } from "@/hooks/useTable";
import { MRT_ColumnDef } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { TPartner } from "@/api/entities/partner.entity";
import { divider } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { TPartnerDisplayColumnSetting } from "@/features/partner/components/partner-list/partner-filter-shared";
import { getDynamicRoute } from "@/utils/url";
import { AppRoutes } from "@/routes/config";
import { useNavigate } from "react-router-dom";
import { truncateWithEllipsis } from "@/utils/string";
import { MAX_PARTNER_LIST_CELL_TEXT_LENGTH } from "@/features/partner/constants/length";

type TPartnerListTableProps = {
  rows: TPartner[];
  isLoading: boolean;
  displayColumnSettings?: TPartnerDisplayColumnSetting[] | null;
};

export const PartnerListTable = ({ rows, isLoading, displayColumnSettings }: TPartnerListTableProps) => {
  const navigate = useNavigate();

  const truncateText = (value: string | null | undefined) => {
    if (!value) return EMPTY_STR.TEXT;
    return truncateWithEllipsis(value, MAX_PARTNER_LIST_CELL_TEXT_LENGTH);
  };

  const toDetailRoute = (partnerId: number) => {
    navigate(getDynamicRoute(AppRoutes.partnerDetail, { partnerId }));
  };

  const columns = useMemo<MRT_ColumnDef<TPartner>[]>(
    () => [
      {
        id: "managementNumber",
        header: "パートナー管理番号",
        accessorKey: "managementNumber",
        size: 190,
        Header: <TableSorting name="managementNumber">パートナー管理番号</TableSorting>,
        Cell: ({ row }) => truncateText(row.original.managementNumber),
      },
      {
        id: "partnerName",
        header: "パートナー名",
        accessorKey: "partnerName",
        size: 262,
        Header: <TableSorting name="partnerName">パートナー名</TableSorting>,
        Cell: ({ row }) => truncateText(row.original.partnerName),
      },
      {
        id: "nationality",
        header: "国籍",
        size: 160,
        Header: <TableSorting name="nationalityName">国籍</TableSorting>,
        Cell: ({ row }) => truncateText(row.original.nationality?.nationalityName),
      },
      {
        id: "location",
        header: "所在地",
        accessorKey: "location",
        size: 262,
        Header: <TableSorting name="location">所在地</TableSorting>,
        Cell: ({ row }) => truncateText(row.original.location || row.original.prefecture),
      },
      {
        id: "contactPerson",
        header: "担当者",
        accessorKey: "contactPerson",
        size: 106,
        Header: <TableSorting name="contactPerson">担当者</TableSorting>,
        Cell: ({ row }) => truncateText(row.original.contactPerson),
      },
      {
        id: "createdAt",
        header: "登録日",
        size: 106,
        Header: <TableSorting name="createdAt">登録日</TableSorting>,
        Cell: ({ row }) => (row.original.createdAt ? dayjs(row.original.createdAt).format(DATE_FORMAT) : EMPTY_STR.TEXT),
      },
    ],
    [],
  );

  const { table } = useTable({
    columns,
    rows,
    onClickRow: (row) => toDetailRoute(row.id),
    muiTableContainerProps: {
      sx: { tableLayout: "fixed" },
    },
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
    renderEmptyRowsFallback: () => <span></span>,
  });

  // Apply column order and visibility via MRT table API (same pattern as JobListTable/CompanyTable)
  useEffect(() => {
    if (!displayColumnSettings) return;

    const visibleIds = displayColumnSettings.filter((s) => s.visible).map((s) => s.id);
    table.setColumnOrder([...visibleIds]);

    const visibilityMap: Record<string, boolean> = {};
    displayColumnSettings.forEach((s) => {
      visibilityMap[s.id] = s.visible;
    });
    table.setColumnVisibility(visibilityMap);
  }, [displayColumnSettings, table]);

  const [heightTable] = useState<string>("calc(100vh - 343px)");

  return (
    <Table table={table} isLoading={isLoading} height={heightTable} minHeight={150} headRowHeight={37} bodyRowHeight={41} borderColor={divider.high} />
  );
};
